import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { API_HTTP_HEADER_NAME } from "@/common/constants/common";
import { TENANT_IDENTIFIER_HEADER_NAME } from "@/common/constants/multitenancy";
import { TypedEventEmitter } from "@/common/eventEmmiters/typedEventEmitter";
import { TelemetryHelper } from "@/common/helpers/telemetry";
import { TypeHelper } from "@/common/helpers/type";
import { IUserI18nSettings } from "@/common/ts/i18n";

import { appCommonConfig } from "../../config/config";
import {
  TeslaAuthApi,
  TeslaConnectionsApi,
  TeslaFeatureInfoApi,
  TeslaVehiclesApi,
} from "./generated/v0-demo";
import {
  AccessoriesApi,
  AccessoryChecksApi,
  AccountAdminApi,
  AccountApi,
  AssessmentFlowsApi,
  AssetSubscriptionPlansApi,
  AssetSubscriptionsApi,
  AssetsApi,
  AssignmentsApi,
  AssistantApi,
  AzureMapsApi,
  CascadeActionsApi,
  ChargesApi,
  ChatActivityApi,
  ChatActivityOverviewsApi,
  ChatEventsApi,
  ChatHistoryApi,
  ChatMessagesApi,
  ChatParticipantsApi,
  ChatUserSettingsApi,
  ChatsApi,
  CommunicationApi,
  ContractsApi,
  CustomSessionsAdminApi,
  CustomTagsApi,
  CustomersApi,
  CustomersInvitesApi,
  DamageCostEvaluationAggregatesApi,
  DamageCostEvaluationsApi,
  DamageDetectionAggregatesApi,
  DamageDetectionsAdminApi,
  DamageDetectionsApi,
  DamageTypesAdminApi,
  DamageTypesApi,
  DataExportApi,
  DataGrantsApi,
  DataImportApi,
  DepartmentsApi,
  DevApi,
  DeviceTokensApi,
  DocumentIntelligenceApi,
  DocumentsApi,
  DomainEventsAdminApi,
  EnumsApi,
  FileApi,
  FilesAdminApi,
  FrontEndAppUpdateInfoAdminApi,
  GeneralBrandingApi,
  GeneralEventLogsApi,
  GeneralHistoryApi,
  GeneralReferenceDataAdminApi,
  GeneralReferenceDataApi,
  GeneralScopesApi,
  GeneralStatusHistoryApi,
  GeneralTagsApi,
  GlobalSearchApi,
  GuidanceNuclinoAdminApi,
  IntegrationApiClientsAdminApi,
  IntegrationApiClientsApi,
  InvitesAdminApi,
  InvitesApi,
  InvoicesAdminApi,
  InvoicesApi,
  LocationsApi,
  MaintenancesApi,
  NegotiationsApi,
  NotificationsAdminApi,
  NotificationsApi,
  OnboardingDefinitionsAdminApi,
  OnboardingDefinitionsApi,
  OnboardingsAdminApi,
  OnboardingsApi,
  OneflowApi,
  OperationsApi,
  PartiesApi,
  PaymentsApi,
  PingApi,
  PoolItemsApi,
  PoolsApi,
  ProductLocationsApi,
  ProfileApi,
  RepairCatalogsApi,
  RepairMaterialsAdminApi,
  RepairMaterialsApi,
  RepairOperationsApi,
  RepairSparePartAdminApi,
  RepairSparePartApi,
  RepairSpecsAdminApi,
  RepairSpecsApi,
  RepairWorkAdminApi,
  RepairWorkApi,
  SpotsApi,
  SubscriptionPlansAdminApi,
  SubscriptionPlansApi,
  SubscriptionsAdminApi,
  SubscriptionsApi,
  SuppliersApi,
  SystemCacheAdminApi,
  TenantAccountApi,
  TenantConnectionRequestsApi,
  TenantConnectionsApi,
  TenantInvitesApi,
  TenantProfileApi,
  TenantRequestsAdminApi,
  TenantRequestsApi,
  TenantStructureApi,
  TenantToTenantApi,
  TenantsAdminApi,
  TenantsApi,
  UserSecretsApi,
  UsersAdminApi,
  UsersApi,
  VehicleBodyTypesAdminApi,
  VehicleBodyTypesApi,
  VehicleDamagesApi,
  VehicleFuelTypesAdminApi,
  VehicleFuelTypesApi,
  VehicleGenerationsAdminApi,
  VehicleGenerationsApi,
  VehicleHistoryApi,
  VehicleMakesAdminApi,
  VehicleMakesApi,
  VehicleModelsAdminApi,
  VehicleModelsApi,
  VehicleModificationsAdminApi,
  VehicleModificationsApi,
  VehiclePartTypesAdminApi,
  VehiclePartTypesApi,
  VehicleScheduleApi,
  VehicleTypesAdminApi,
  VehicleTypesApi,
  VehicleVisualModelsAdminApi,
  VehicleVisualModelsApi,
  VehiclesApi,
  VisualInspectionsApi,
  WashesApi,
  WebPushWebhookApi,
  WebhooksApi,
  WheelOperationsApi,
  WidgetDataAdminApi,
  WidgetDataApi,
} from "./generated/v1/api";
import { BaseAPI } from "./generated/v1/base";
import { Configuration } from "./generated/v1/configuration";

/** Represents last known API connection state (whether it was available or not). */
export const ApiConnectionStatus = {
  Connected: "Connected",
  Disconnected: "Disconnected",
} as const;
export type ApiConnectionStatus = (typeof ApiConnectionStatus)[keyof typeof ApiConnectionStatus];

type ApiResponseHandler = (
  error?: AxiosError<any, any>,
  response?: AxiosResponse<any, any>,
) => void | Promise<void>;
type ApiSuccessHandler = (response: AxiosResponse<any, any>) => void | Promise<void>;
type ApiErrorHandler = (error: AxiosError<any, any>) => void | Promise<void>;

export class ApiClient extends TypedEventEmitter<{
  // list of supported events
  connectionStateChange: ApiConnectionStatus;
  connected: undefined;
  disconnected: undefined;
}> {
  /** Manually managed Axios instance for configuration and tweaking purposes. */
  private _axiosInstance: AxiosInstance = axios.create();

  private _accessToken: string;
  private _connectionStatus: ApiConnectionStatus = ApiConnectionStatus.Disconnected;
  private _apiConfiguration: Configuration;
  private apiResponseHandlers: ApiResponseHandler[] = [];
  private apiSuccessHandlers: ApiSuccessHandler[] = [];
  private apiErrorHandlers: ApiErrorHandler[] = [];

  public pingApi!: PingApi;
  public devApi!: DevApi;

  public accountAdminApi!: AccountAdminApi;
  public tenantsAdminApi!: TenantsAdminApi;
  public usersAdminApi!: UsersAdminApi;
  public invoicesAdminApi!: InvoicesAdminApi;
  public notificationsAdminApi!: NotificationsAdminApi;
  public subscriptionPlansAdminApi!: SubscriptionPlansAdminApi;
  public subscriptionsAdminApi!: SubscriptionsAdminApi;
  public generalReferenceDataAdminApi!: GeneralReferenceDataAdminApi;
  public vehicleTypesAdminApi!: VehicleTypesAdminApi;
  public vehicleFuelTypesAdminApi!: VehicleFuelTypesAdminApi;
  public vehicleMakesAdminApi!: VehicleMakesAdminApi;
  public vehicleModelsAdminApi!: VehicleModelsAdminApi;
  public vehicleGenerationsAdminApi!: VehicleGenerationsAdminApi;
  public vehicleModificationsAdminApi!: VehicleModificationsAdminApi;
  public vehicleBodyTypesAdminApi!: VehicleBodyTypesAdminApi;
  public damageTypesAdminApi!: DamageTypesAdminApi;
  public vehiclePartTypesAdminApi!: VehiclePartTypesAdminApi;
  public vehicleVisualModelsAdminApi!: VehicleVisualModelsAdminApi;
  public repairMaterialsAdminApi!: RepairMaterialsAdminApi;
  public repairWorkAdminApi!: RepairWorkAdminApi;
  public repairSparePartAdminApi!: RepairSparePartAdminApi;
  public repairSpecsAdminApi!: RepairSpecsAdminApi;
  public systemCacheAdminApi!: SystemCacheAdminApi;
  public customSessionsAdminApi!: CustomSessionsAdminApi;
  public integrationApiClientsAdminApi!: IntegrationApiClientsAdminApi;
  public invitesAdminApi!: InvitesAdminApi;
  public domainEventsAdminApi!: DomainEventsAdminApi;
  public tenantRequestsAdminApi!: TenantRequestsAdminApi;
  public filesAdminApi!: FilesAdminApi;
  public damageDetectionsAdminApi!: DamageDetectionsAdminApi;
  public widgetDataAdminApi!: WidgetDataAdminApi;
  public frontEndAppUpdateInfoAdminApi!: FrontEndAppUpdateInfoAdminApi;
  public guidanceNuclinoAdminApi!: GuidanceNuclinoAdminApi;
  public onboardingDefinitionsAdminApi!: OnboardingDefinitionsAdminApi;
  public onboardingsAdminApi!: OnboardingsAdminApi;

  public accountApi!: AccountApi;
  public invitesApi!: InvitesApi;
  public invoicesApi!: InvoicesApi;
  public paymentsApi!: PaymentsApi;
  public profileApi!: ProfileApi;
  public tenantProfileApi!: TenantProfileApi;
  public subscriptionsApi!: SubscriptionsApi;
  public subscriptionPlansApi!: SubscriptionPlansApi;
  public tenantAccountApi!: TenantAccountApi;
  public tenantInvitesApi!: TenantInvitesApi;
  public usersApi!: UsersApi;
  public vehiclesApi!: VehiclesApi;
  public vehicleScheduleApi!: VehicleScheduleApi;
  public vehicleHistoryApi!: VehicleHistoryApi;
  public accessoriesApi!: AccessoriesApi;
  public customersApi!: CustomersApi;
  public suppliersApi!: SuppliersApi;
  public tenantCustomerInvitesApi!: CustomersInvitesApi;
  public contractsApi!: ContractsApi;
  public assessmentFlowsApi!: AssessmentFlowsApi;
  public vehicleTypesApi!: VehicleTypesApi;
  public vehicleFuelTypesApi!: VehicleFuelTypesApi;
  public vehicleModelsApi!: VehicleModelsApi;
  public vehicleMakesApi!: VehicleMakesApi;
  public vehicleGenerationsApi!: VehicleGenerationsApi;
  public vehicleModificationsApi!: VehicleModificationsApi;
  public vehicleBodyTypesApi!: VehicleBodyTypesApi;
  public vehiclePartTypesApi!: VehiclePartTypesApi;
  public vehicleVisualModelsApi!: VehicleVisualModelsApi;
  public vehicleDamagesApi!: VehicleDamagesApi;
  public damageTypesApi!: DamageTypesApi;
  public departmentsApi!: DepartmentsApi;
  public tenantStructureApi!: TenantStructureApi;
  public locationsApi!: LocationsApi;
  public productLocationsApi!: ProductLocationsApi;
  public spotsApi!: SpotsApi;
  public assetSubscriptionPlansApi!: AssetSubscriptionPlansApi;
  public assetSubscriptionsApi!: AssetSubscriptionsApi;
  public generalReferenceDataApi!: GeneralReferenceDataApi;
  public visualInspectionsApi!: VisualInspectionsApi;
  public damageDetectionsApi!: DamageDetectionsApi;
  public operationsApi!: OperationsApi;
  public damageDetectionAggregatesApi!: DamageDetectionAggregatesApi;
  public damageCostEvaluationAggregatesApi!: DamageCostEvaluationAggregatesApi;
  public damageCostEvaluationsApi!: DamageCostEvaluationsApi;
  public accessoryChecksApi!: AccessoryChecksApi;
  public repairOperationsApi!: RepairOperationsApi;
  public wheelOperationsApi!: WheelOperationsApi;
  public washesApi!: WashesApi;
  public maintenancesApi!: MaintenancesApi;
  public dataImportApi!: DataImportApi;
  public dataExportApi!: DataExportApi;
  public filesApi!: FileApi;
  public enumsApi!: EnumsApi;
  public notificationsApi!: NotificationsApi;
  public communicationApi!: CommunicationApi;
  public chatsApi!: ChatsApi;
  public negotiationsApi!: NegotiationsApi;
  public chatParticipantsApi!: ChatParticipantsApi;
  public chatMessagesApi!: ChatMessagesApi;
  public chatEventsApi!: ChatEventsApi;
  public chatHistoryApi!: ChatHistoryApi;
  public chatUserSettingsApi!: ChatUserSettingsApi;
  public chatActivityApi!: ChatActivityApi;
  public chatActivityOverviewsApi!: ChatActivityOverviewsApi;
  public documentsApi!: DocumentsApi;
  public globalSearchApi!: GlobalSearchApi;
  public repairMaterialsApi!: RepairMaterialsApi;
  public repairWorkApi!: RepairWorkApi;
  public repairSparePartApi!: RepairSparePartApi;
  public repairSpecsApi!: RepairSpecsApi;
  public repairCatalogsApi!: RepairCatalogsApi;
  public partiesApi!: PartiesApi;
  public generalTagsApi!: GeneralTagsApi;
  public generalEventLogsApi!: GeneralEventLogsApi;
  public generalScopesApi!: GeneralScopesApi;
  public generalBrandingApi!: GeneralBrandingApi;
  public deviceTokensApi!: DeviceTokensApi;
  public webPushWebhookApi!: WebPushWebhookApi;
  public poolsApi!: PoolsApi;
  public poolItemsApi!: PoolItemsApi;
  public assetsApi!: AssetsApi;
  public integrationApiClientsApi!: IntegrationApiClientsApi;
  public tenantToTenantApi!: TenantToTenantApi;
  public tenantConnectionRequestsApi!: TenantConnectionRequestsApi;
  public tenantConnectionsApi!: TenantConnectionsApi;
  public tenantRequestsApi!: TenantRequestsApi;
  public dataGrantsApi!: DataGrantsApi;
  public tenantsApi!: TenantsApi;
  public userSecretsApi!: UserSecretsApi;
  public customTagsApi!: CustomTagsApi;
  public webhooksApi!: WebhooksApi;
  public cascadeActionsApi!: CascadeActionsApi;
  public generalHistoryApi!: GeneralHistoryApi;
  public generalStatusHistoryApi!: GeneralStatusHistoryApi;
  public teslaFeatureInfoApi!: TeslaFeatureInfoApi;
  public teslaAuthApi!: TeslaAuthApi;
  public teslaConnectionsApi!: TeslaConnectionsApi;
  public teslaVehiclesApi!: TeslaVehiclesApi;
  public widgetDataApi!: WidgetDataApi;
  public assignmentsApi!: AssignmentsApi;
  public azureMapsApi!: AzureMapsApi;
  public assistantApi!: AssistantApi;
  public chargesApi!: ChargesApi;
  public onboardingDefinitionsApi!: OnboardingDefinitionsApi;
  public onboardingsApi!: OnboardingsApi;
  public documentIntelligenceApi!: DocumentIntelligenceApi;
  public oneflowApi!: OneflowApi;

  constructor(apiConfiguration: Configuration) {
    super();
    this._accessToken = "";
    this._apiConfiguration = apiConfiguration;
    this.registerAxiosInterceptors();
    this.regenerateApiWithNewConfiguration(apiConfiguration);
    this.startApiConnectionTracking();
  }

  public get connectionStatus(): ApiConnectionStatus {
    return this._connectionStatus;
  }

  private registerAxiosInterceptors(): void {
    // add request interceptor to ensure fresh telemetry is sent in each request.
    this._axiosInstance.interceptors.request.clear();
    this._axiosInstance.interceptors.request.use(
      (config) => {
        // do something before request is sent
        config.headers[API_HTTP_HEADER_NAME.FE_TELEMETRY_DATA] =
          TelemetryHelper.getTelemetryDataInputEncodedForHttpHeader();
        return config;
      },
      (error) => {
        // do something with request error
        return Promise.reject(error);
      },
    );
  }

  private regenerateApiWithNewConfiguration(apiConfiguration: Configuration) {
    this._apiConfiguration = apiConfiguration;

    this._apiConfiguration.baseOptions ||= {};
    const axiosConfig = this._apiConfiguration.baseOptions as AxiosRequestConfig;

    // enable credentials so we can read Cookies set by the API server
    axiosConfig.withCredentials = true;

    this.pingApi = this.withApiProxy(
      new PingApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.devApi = this.withApiProxy(
      new DevApi(this._apiConfiguration, undefined, this._axiosInstance),
    );

    this.accountAdminApi = this.withApiProxy(
      new AccountAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantsAdminApi = this.withApiProxy(
      new TenantsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.usersAdminApi = this.withApiProxy(
      new UsersAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.invoicesAdminApi = this.withApiProxy(
      new InvoicesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.notificationsAdminApi = this.withApiProxy(
      new NotificationsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.subscriptionPlansAdminApi = this.withApiProxy(
      new SubscriptionPlansAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.subscriptionsAdminApi = this.withApiProxy(
      new SubscriptionsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalReferenceDataAdminApi = this.withApiProxy(
      new GeneralReferenceDataAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleTypesAdminApi = this.withApiProxy(
      new VehicleTypesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleFuelTypesAdminApi = this.withApiProxy(
      new VehicleFuelTypesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleMakesAdminApi = this.withApiProxy(
      new VehicleMakesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleModelsAdminApi = this.withApiProxy(
      new VehicleModelsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleGenerationsAdminApi = this.withApiProxy(
      new VehicleGenerationsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleModificationsAdminApi = this.withApiProxy(
      new VehicleModificationsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleBodyTypesAdminApi = this.withApiProxy(
      new VehicleBodyTypesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageTypesAdminApi = this.withApiProxy(
      new DamageTypesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehiclePartTypesAdminApi = this.withApiProxy(
      new VehiclePartTypesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleVisualModelsAdminApi = this.withApiProxy(
      new VehicleVisualModelsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairMaterialsAdminApi = this.withApiProxy(
      new RepairMaterialsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairWorkAdminApi = this.withApiProxy(
      new RepairWorkAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairSparePartAdminApi = this.withApiProxy(
      new RepairSparePartAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairSpecsAdminApi = this.withApiProxy(
      new RepairSpecsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.systemCacheAdminApi = this.withApiProxy(
      new SystemCacheAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.customSessionsAdminApi = this.withApiProxy(
      new CustomSessionsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.integrationApiClientsAdminApi = this.withApiProxy(
      new IntegrationApiClientsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.invitesAdminApi = this.withApiProxy(
      new InvitesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.domainEventsAdminApi = this.withApiProxy(
      new DomainEventsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantRequestsAdminApi = this.withApiProxy(
      new TenantRequestsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.filesAdminApi = this.withApiProxy(
      new FilesAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageDetectionsAdminApi = this.withApiProxy(
      new DamageDetectionsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.widgetDataAdminApi = this.withApiProxy(
      new WidgetDataAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.frontEndAppUpdateInfoAdminApi = this.withApiProxy(
      new FrontEndAppUpdateInfoAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.guidanceNuclinoAdminApi = this.withApiProxy(
      new GuidanceNuclinoAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.onboardingDefinitionsAdminApi = this.withApiProxy(
      new OnboardingDefinitionsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.onboardingsAdminApi = this.withApiProxy(
      new OnboardingsAdminApi(this._apiConfiguration, undefined, this._axiosInstance),
    );

    this.accountApi = this.withApiProxy(
      new AccountApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.invitesApi = this.withApiProxy(
      new InvitesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.invoicesApi = this.withApiProxy(
      new InvoicesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.paymentsApi = this.withApiProxy(
      new PaymentsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.profileApi = this.withApiProxy(
      new ProfileApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantProfileApi = this.withApiProxy(
      new TenantProfileApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.subscriptionsApi = this.withApiProxy(
      new SubscriptionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.subscriptionPlansApi = this.withApiProxy(
      new SubscriptionPlansApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantAccountApi = this.withApiProxy(
      new TenantAccountApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantInvitesApi = this.withApiProxy(
      new TenantInvitesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.usersApi = this.withApiProxy(
      new UsersApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehiclesApi = this.withApiProxy(
      new VehiclesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleScheduleApi = this.withApiProxy(
      new VehicleScheduleApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleHistoryApi = this.withApiProxy(
      new VehicleHistoryApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.accessoriesApi = this.withApiProxy(
      new AccessoriesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.customersApi = this.withApiProxy(
      new CustomersApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.suppliersApi = this.withApiProxy(
      new SuppliersApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantCustomerInvitesApi = this.withApiProxy(
      new CustomersInvitesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.contractsApi = this.withApiProxy(
      new ContractsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.assessmentFlowsApi = this.withApiProxy(
      new AssessmentFlowsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleTypesApi = this.withApiProxy(
      new VehicleTypesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleFuelTypesApi = this.withApiProxy(
      new VehicleFuelTypesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleModelsApi = this.withApiProxy(
      new VehicleModelsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleMakesApi = this.withApiProxy(
      new VehicleMakesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleGenerationsApi = this.withApiProxy(
      new VehicleGenerationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleModificationsApi = this.withApiProxy(
      new VehicleModificationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleBodyTypesApi = this.withApiProxy(
      new VehicleBodyTypesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehiclePartTypesApi = this.withApiProxy(
      new VehiclePartTypesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleVisualModelsApi = this.withApiProxy(
      new VehicleVisualModelsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.vehicleDamagesApi = this.withApiProxy(
      new VehicleDamagesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageTypesApi = this.withApiProxy(
      new DamageTypesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.departmentsApi = this.withApiProxy(
      new DepartmentsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantStructureApi = this.withApiProxy(
      new TenantStructureApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.locationsApi = this.withApiProxy(
      new LocationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.productLocationsApi = this.withApiProxy(
      new ProductLocationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.spotsApi = this.withApiProxy(
      new SpotsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.assetSubscriptionPlansApi = this.withApiProxy(
      new AssetSubscriptionPlansApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.assetSubscriptionsApi = this.withApiProxy(
      new AssetSubscriptionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalReferenceDataApi = this.withApiProxy(
      new GeneralReferenceDataApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.visualInspectionsApi = this.withApiProxy(
      new VisualInspectionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageDetectionsApi = this.withApiProxy(
      new DamageDetectionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.operationsApi = this.withApiProxy(
      new OperationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageDetectionAggregatesApi = this.withApiProxy(
      new DamageDetectionAggregatesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageCostEvaluationAggregatesApi = this.withApiProxy(
      new DamageCostEvaluationAggregatesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.damageCostEvaluationsApi = this.withApiProxy(
      new DamageCostEvaluationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.wheelOperationsApi = this.withApiProxy(
      new WheelOperationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.dataImportApi = this.withApiProxy(
      new DataImportApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.dataExportApi = this.withApiProxy(
      new DataExportApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.accessoryChecksApi = this.withApiProxy(
      new AccessoryChecksApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairOperationsApi = this.withApiProxy(
      new RepairOperationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.washesApi = this.withApiProxy(
      new WashesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.maintenancesApi = this.withApiProxy(
      new MaintenancesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.filesApi = this.withApiProxy(
      new FileApi(this._apiConfiguration, undefined, this._axiosInstance),
    );

    this.enumsApi = this.withApiProxy(
      new EnumsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.notificationsApi = this.withApiProxy(
      new NotificationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.communicationApi = this.withApiProxy(
      new CommunicationApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatsApi = this.withApiProxy(
      new ChatsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.negotiationsApi = this.withApiProxy(
      new NegotiationsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatParticipantsApi = this.withApiProxy(
      new ChatParticipantsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatMessagesApi = this.withApiProxy(
      new ChatMessagesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatEventsApi = this.withApiProxy(
      new ChatEventsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatHistoryApi = this.withApiProxy(
      new ChatHistoryApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatUserSettingsApi = this.withApiProxy(
      new ChatUserSettingsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatActivityApi = this.withApiProxy(
      new ChatActivityApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chatActivityOverviewsApi = this.withApiProxy(
      new ChatActivityOverviewsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.documentsApi = this.withApiProxy(
      new DocumentsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.globalSearchApi = this.withApiProxy(
      new GlobalSearchApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairMaterialsApi = this.withApiProxy(
      new RepairMaterialsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairWorkApi = this.withApiProxy(
      new RepairWorkApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairSparePartApi = this.withApiProxy(
      new RepairSparePartApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairSpecsApi = this.withApiProxy(
      new RepairSpecsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.repairCatalogsApi = this.withApiProxy(
      new RepairCatalogsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.partiesApi = this.withApiProxy(
      new PartiesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalTagsApi = this.withApiProxy(
      new GeneralTagsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalEventLogsApi = this.withApiProxy(
      new GeneralEventLogsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalScopesApi = this.withApiProxy(
      new GeneralScopesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalBrandingApi = this.withApiProxy(
      new GeneralBrandingApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.deviceTokensApi = this.withApiProxy(
      new DeviceTokensApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.webPushWebhookApi = this.withApiProxy(
      new WebPushWebhookApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.poolsApi = this.withApiProxy(
      new PoolsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.poolItemsApi = this.withApiProxy(
      new PoolItemsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.assetsApi = this.withApiProxy(
      new AssetsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.integrationApiClientsApi = this.withApiProxy(
      new IntegrationApiClientsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantToTenantApi = this.withApiProxy(
      new TenantToTenantApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantConnectionRequestsApi = this.withApiProxy(
      new TenantConnectionRequestsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantConnectionsApi = this.withApiProxy(
      new TenantConnectionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantRequestsApi = this.withApiProxy(
      new TenantRequestsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.dataGrantsApi = this.withApiProxy(
      new DataGrantsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.tenantsApi = this.withApiProxy(
      new TenantsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.userSecretsApi = this.withApiProxy(
      new UserSecretsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.customTagsApi = this.withApiProxy(
      new CustomTagsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.webhooksApi = this.withApiProxy(
      new WebhooksApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.cascadeActionsApi = this.withApiProxy(
      new CascadeActionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalHistoryApi = this.withApiProxy(
      new GeneralHistoryApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.generalStatusHistoryApi = this.withApiProxy(
      new GeneralStatusHistoryApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.teslaFeatureInfoApi = this.withApiProxy(
      new TeslaFeatureInfoApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.teslaAuthApi = this.withApiProxy(
      new TeslaAuthApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.teslaConnectionsApi = this.withApiProxy(
      new TeslaConnectionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.teslaVehiclesApi = this.withApiProxy(
      new TeslaVehiclesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.widgetDataApi = this.withApiProxy(
      new WidgetDataApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.assignmentsApi = this.withApiProxy(
      new AssignmentsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.assistantApi = this.withApiProxy(
      new AssistantApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.azureMapsApi = this.withApiProxy(
      new AzureMapsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.chargesApi = this.withApiProxy(
      new ChargesApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.onboardingDefinitionsApi = this.withApiProxy(
      new OnboardingDefinitionsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.onboardingsApi = this.withApiProxy(
      new OnboardingsApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.documentIntelligenceApi = this.withApiProxy(
      new DocumentIntelligenceApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
    this.oneflowApi = this.withApiProxy(
      new OneflowApi(this._apiConfiguration, undefined, this._axiosInstance),
    );
  }

  public getAccessToken(): string {
    return this._accessToken;
  }

  /** Sets access token to be used for future requests. */
  public updateAccessToken(token: string): void {
    this._accessToken = token;
    const configuration = new Configuration({
      ...this._apiConfiguration,
      accessToken: token,
    });
    this.regenerateApiWithNewConfiguration(configuration);
  }

  /** Sets tenant identifier to be used for future requests. */
  public updateTenantIdentifier(identifier: string): void {
    const configuration = new Configuration({
      ...this._apiConfiguration,
    });
    const axiosConfig = (configuration.baseOptions || { headers: {} }) as AxiosRequestConfig;
    axiosConfig.headers ||= {};

    // NB: ensure headers are non-empty here so they can be overwritten by specific API methods
    axiosConfig.headers[`${TENANT_IDENTIFIER_HEADER_NAME}`] = identifier;
    if (TypeHelper.isEmpty(axiosConfig.headers[`${TENANT_IDENTIFIER_HEADER_NAME}`])) {
      delete axiosConfig.headers[`${TENANT_IDENTIFIER_HEADER_NAME}`];
    }

    this.regenerateApiWithNewConfiguration(configuration);
  }

  /** Sets SignalR connection ids to be used for future requests. */
  public updateSignalConnectionIds(props: {
    chatHubConnectionId?: string | null;
    notificationHubConnectionId?: string | null;
    dataUpdatesHubConnectionId?: string | null;
  }): void {
    const configuration = new Configuration({
      ...this._apiConfiguration,
    });
    const axiosConfig = (configuration.baseOptions || { headers: {} }) as AxiosRequestConfig;
    axiosConfig.headers ||= {};

    axiosConfig.headers[API_HTTP_HEADER_NAME.SIGNALR_CHATHUB_CONNECTIONID] =
      props.chatHubConnectionId ||
      axiosConfig.headers[API_HTTP_HEADER_NAME.SIGNALR_CHATHUB_CONNECTIONID];
    axiosConfig.headers[API_HTTP_HEADER_NAME.SIGNALR_NOTIFICATIONHUB_CONNECTIONID] =
      props.notificationHubConnectionId ||
      axiosConfig.headers[API_HTTP_HEADER_NAME.SIGNALR_NOTIFICATIONHUB_CONNECTIONID];
    axiosConfig.headers[API_HTTP_HEADER_NAME.SIGNALR_DATAUPDATESHUB_CONNECTIONID] =
      props.dataUpdatesHubConnectionId ||
      axiosConfig.headers[API_HTTP_HEADER_NAME.SIGNALR_DATAUPDATESHUB_CONNECTIONID];
    this.regenerateApiWithNewConfiguration(configuration);
  }

  /** Sets culture to be used for future requests. */
  public updateUserI18nSettings(i18nSettings: IUserI18nSettings): void {
    const configuration = new Configuration({
      ...this._apiConfiguration,
    });
    const axiosConfig = (configuration.baseOptions || { headers: {} }) as AxiosRequestConfig;
    axiosConfig.headers ||= {};

    axiosConfig.headers[`${API_HTTP_HEADER_NAME.CULTURE_NAME}`] = i18nSettings.cultureName;
    axiosConfig.headers[`${API_HTTP_HEADER_NAME.TIME_ZONE_ID}`] = i18nSettings.tzId;
    this.regenerateApiWithNewConfiguration(configuration);
  }

  /** Registers handler that is called after each successful or error response from generated API client. */
  public registerApiResponseHandler(handler: ApiResponseHandler): void {
    this.apiResponseHandlers.push(handler);
  }

  /** Registers handler that is called after each successful response from generated API client. */
  public registerApiSuccessHandler(handler: ApiSuccessHandler): void {
    this.apiSuccessHandlers.push(handler);
  }

  /** Registers handler that is called after each error response from generated API client. */
  public registerApiErrorHandler(handler: ApiErrorHandler): void {
    this.apiErrorHandlers.push(handler);
  }

  public removeApiHandler(handler: ApiResponseHandler | ApiSuccessHandler | ApiErrorHandler): void {
    const index1 = this.apiResponseHandlers.findIndex((x) => x === handler);
    const index2 = this.apiSuccessHandlers.findIndex((x) => x === handler);
    const index3 = this.apiErrorHandlers.findIndex((x) => x === handler);

    index1 !== -1 && this.apiResponseHandlers.splice(index1, 1);
    index2 !== -1 && this.apiSuccessHandlers.splice(index2, 1);
    index3 !== -1 && this.apiErrorHandlers.splice(index3, 1);
  }

  private withApiProxy<Api = BaseAPI>(api: Api): Api {
    const apiClient = this;

    const proxy: Api = new Proxy(api as any, {
      get(target, prop, receiver) {
        const value: any = (target as any)[prop];
        const isFunction = value instanceof Function;

        if (isFunction) {
          // decorate returned function to add custom behavior after its execution
          return function (...args: any[]) {
            const result = value.apply(target, args);
            if (result instanceof Promise) {
              return result
                .then((response: AxiosResponse<any, any>) => {
                  // call registered handlers without waiting for them to execute
                  apiClient.apiResponseHandlers.map((handler) => handler(undefined, response));
                  apiClient.apiSuccessHandlers.map((handler) => handler(response));

                  return response;
                })
                .catch((err: any) => {
                  if (err instanceof AxiosError) {
                    // call registered handlers without waiting for them to execute
                    apiClient.apiResponseHandlers.map((handler) => handler(err, undefined));
                    apiClient.apiErrorHandlers.map((handler) => handler(err));
                  }

                  throw err;
                });
            } else {
              return result;
            }
          };
        }

        return value;
      },
    }) as Api;

    return proxy;
  }

  /** Checks request responses to figure out whether Network Error occurred. */
  private startApiConnectionTracking() {
    const successHandler: ApiSuccessHandler = (() => {
      // console.log("ApiClient.ApiSuccessHandler");
      this._connectionStatus = ApiConnectionStatus.Connected;
      this.emit("connectionStateChange", this._connectionStatus);
      this.emit("connected", undefined);
    }).bind(this);

    const errorHandler: ApiErrorHandler = ((err: AxiosError) => {
      // console.log("ApiClient.ApiErrorHandler", err);
      const isNetworkError = err.code === "ERR_NETWORK" || err.message === "Network Error";
      if (isNetworkError) {
        // console.log("API connection tracker. Network error:", err);
        this._connectionStatus = ApiConnectionStatus.Disconnected;
        this.emit("connectionStateChange", this._connectionStatus);
        this.emit("disconnected", undefined);
      }
    }).bind(this);

    this.registerApiSuccessHandler(successHandler);
    this.registerApiErrorHandler(errorHandler);
  }
}

export const apiClient = new ApiClient(
  new Configuration({
    basePath: appCommonConfig.apiUrl || window.location.origin,
    baseOptions: {
      // NB: base headers must not be set with empty values to allow their rewrite in specific API clients local calls.
      // generated code prioritizes headers in the next order: options.headers, headersFromBaseOptions, localVarHeaderParameter
      // generated code: headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers}
      headers: {
        [`${API_HTTP_HEADER_NAME.APP_VERSION}`]: appCommonConfig.version,
        // [`${TENANT_IDENTIFIER_HEADER_NAME}`]: "",
      },
    },
  }),
);
